/**
 * Push deep-link bridge — the standalone twin of the one in
 * `root/registerServiceWorker.js`.
 *
 * `public/sw.js` handles a notification tap by focusing an open tab and posting
 * `metaship:push-navigate` on a MessageChannel. Without a listener that answers
 * on the reply port, the worker waits 700 ms and then hard-navigates the tab — a
 * full reload of the shell for every tap.
 *
 * `metaship:push-resubscribe` — the browser rotated the subscription. The worker
 * holds neither the API base URL nor the auth token, so it is re-emitted as a DOM
 * event that the shared `@notifications` composable finishes.
 *
 * Installed BEFORE the tenant resolve awaits: a tap can focus a tab that is still
 * booting, and a listener attached later misses the message entirely.
 */
export function installPushBridge(): void {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    const data = (event.data ?? {}) as { type?: string; url?: unknown; oldEndpoint?: string; subscription?: unknown };

    if (data.type === "metaship:push-navigate" && typeof data.url === "string") {
      try {
        const target = new URL(data.url, window.location.origin);
        // Same-origin only. The worker checks this too; the check stays local to
        // the code that navigates.
        if (target.origin !== window.location.origin) return;
        const here = window.location.pathname + window.location.search + window.location.hash;
        const there = target.pathname + target.search + target.hash;
        // single-spa patches pushState, so this reroutes to the target module.
        if (here !== there) window.history.pushState({}, "", there);
        // Always dispatch, even on the same URL: the module may already have
        // consumed and stripped a transient deep link (?conversation= / ?task=).
        window.dispatchEvent(new CustomEvent("metaship:navigate", { detail: { source: "push" } }));
        event.ports?.[0]?.postMessage({ handled: true });
      } catch {
        /* a malformed URL must not take the page down; the worker falls back to navigate() */
      }
      return;
    }

    if (data.type === "metaship:push-resubscribe") {
      window.dispatchEvent(
        new CustomEvent("metaship:push-resubscribe", {
          detail: { oldEndpoint: data.oldEndpoint || null, subscription: data.subscription || null },
        }),
      );
    }
  });
}
